import React, {useRef, useEffect, useState, useContext} from 'react';
import {Handle} from 'reactflow';
import {useSelector, shallowEqual} from 'react-redux';
import {Chatbot} from '../../states/actions';
import {User} from '../../states/actions';
import {InputComponent, SelectComp, NodeComponent, PreviewComponent, onEdited} from './Components'
import { GlobalStateContext } from '../GlobalState';
import {acceptedFileTypes, validations, moneyCurrency} from '../../config';
import {createSelectNodeData} from '../../states/selectors/chatbotSelectors';
import Select from 'react-select';

const zoomSelector = (s) => s.transform[2];

export const InputUploadBlock = React.memo((node) => {
  const { globalState } = useContext(GlobalStateContext);  
  const {block, start, review, progress} = useSelector(createSelectNodeData(node.id), shallowEqual);
  const bot = {progress};
  const [previewMode, setPreviewMode] = useState(globalState.preview ? undefined : false);

  useEffect(() => {
    setPreviewMode(globalState.preview);
  }, [globalState.preview]);

  if (!block) return null;

  const {data} = block;

  return (
    <>
      {!previewMode && (
        <NodeComponent bot={bot} block={block} name="Input+Upload" icon="input" icons={['input', 'upload']} data={data} node={node} start={start} />
      )}
      {previewMode && (
        <PreviewComponent bot={bot} data={data} node={node} start={start}/>
      )}
    </>
  );
});

export const InputUploadEditor = ({nodeid, block, data, variableoptions, language}) => {
  const variables_inputtype = [
    { label: 'Input', value: 'input', content: 'input' },
    { label: 'Textarea', value: 'textarea', content: 'textarea' },
    { label: 'Email', value: 'email', content: 'email' },
    { label: 'Phone', value: 'phone', content: 'phone' },
    { label: 'Number', value: 'number', content: 'number' },
    { label: 'Signature', value: 'signature', content: 'signature' },
  ];

  if (!block) {
    return null;
  }

  const handleBlur = (e) => {   
    Chatbot.setBlockData(block.id, {[e.target.name]: e.target.value});
  };

  return(
    <>
      <div className='section'>INPUT</div>

      <SelectComp data={data} name="Input type" dataname="inputtype" options={variables_inputtype} block={block}/>
      
      <InputComponent data={data} name="Character limit" dataname="characterlimit" onBlur={handleBlur}/>

      <SelectComp data={data} name="Validation Rule" dataname="validation" placeholder="Select validation rule" options={validations} block={block}/>

      {data.validation === 'custom' && (
        <InputComponent data={data} name="Custom rule" dataname="customrule" onBlur={handleBlur}/>
      )}

      {data.validation === 'money' && (
        <SelectComp data={data} name="Choose Currency" dataname="currency" placeholder="Choose currency" options={moneyCurrency} block={block}/>
      )}

      <InputComponent data={data} name="Validation message" dataname={language === 'en' ? 'validationMessage_en' : language === 'ru' ? 'validationMessage_ru' : 'validationMessage'} language={language} onBlur={handleBlur}/>

      <br/><br/><br/>
      
      <InputComponent data={data} name="Input name" dataname={language === 'en' ? 'inputname_en' : language === 'ru' ? 'inputname_ru' : 'inputname'} language={language} onBlur={handleBlur}/>

      <InputComponent data={data} name="Hint message" dataname={language === 'en' ? 'hint_en' : language === 'ru' ? 'hint_ru' : 'hint'} language={language} onBlur={handleBlur}/>

      <InputComponent icon="*" data={data} name="Variable (text)" dataname="variable" onBlur={handleBlur}/>

      <div className='section'>UPLOAD</div>

      <InputComponent data={data} name="Upload file name" dataname="filePrefix" onBlur={handleBlur}/>
      <InputComponent data={data} name="File size limit (mb)" dataname="sizeLimit" onBlur={handleBlur}/>
      <InputComponent
        data={data}
        name="Max files (0 = unlimited, 1 = single file for AI)"
        dataname="maxFiles"
        onBlur={handleBlur}
      />

      <div className="form-check my-3">
        <input
          type="checkbox"
          id="inputuploadBackgroundUpload"
          name="backgroundUpload"
          className="form-check-input"
          checked={!!data.backgroundUpload}
          onChange={(e) => {
            const checked = e.target.checked;
            const patch = {backgroundUpload: checked};
            if (!checked) {
              patch.showBackgroundUploadProgress = true;
            }
            Chatbot.setBlockData(block.id, patch);
            onEdited();
          }}
        />
        <label htmlFor="inputuploadBackgroundUpload" className="form-check-label">
          Run in background
        </label>
        <div className="form-text">
          User continues immediately; later blocks wait automatically if they need the files.
        </div>
      </div>

      {!!data.backgroundUpload && (
        <div className="form-check my-3 ms-4">
          <input
            type="checkbox"
            id="inputuploadShowBackgroundProgress"
            name="showBackgroundUploadProgress"
            className="form-check-input"
            checked={data.showBackgroundUploadProgress !== false}
            onChange={(e) => {
              Chatbot.setBlockData(block.id, {showBackgroundUploadProgress: e.target.checked});
              onEdited();
            }}
          />
          <label htmlFor="inputuploadShowBackgroundProgress" className="form-check-label">
            Show run in background message and progress
          </label>
          <div className="form-text">
            When off, files still upload in the background but the user does not see the top progress bar or uploading message.
          </div>
        </div>
      )}

      <label htmlFor="fileSizeLimit" className="form-label">
        Accepted File Type
      </label>
      <Select
        id="acceptedTypes"
        name="acceptedTypes"
        placeholder="Select File Type"
        className=" form-control-sm editor-select"
        classNamePrefix="editor-select"
        isMulti
        options={acceptedFileTypes}
        value={acceptedFileTypes.filter((item) => data?.acceptedTypes?.includes(item.value))}
        onChange={(option) => {
          Chatbot.setBlockData(block.id, {assign: {...data, acceptedTypes: option.map((i) => i.value)}});
        }}
      />

      <InputComponent icon="*" data={data} name="Variable (upload)" dataname="uploadVariable" onBlur={handleBlur}/>
    </>
  )
}
